import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { Bookmark, ChevronLeft, ChevronRight, ClipboardCheck, DownloadCloud, Eye, LoaderCircle, Save, Search, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type TenderFeasibilityRun = {
  id: string;
  tender_id: string;
  status: "not_run" | "running" | "succeeded" | "failed";
  score: number | null;
  pros_json: string;
  cons_json: string;
  summary: string | null;
  error: string | null;
  created_at: string;
};

type TenderMaterialPlatformInfo = {
  domain: string;
  platform: "ethics.dk" | "unknown";
  url: string;
  supportedAutoDownload: boolean;
};

type TenderSearchResult = {
  id: string | null;
  source: "udbud.dk";
  noticeId: string;
  noticeVersion: string;
  noticePublicationNumber: string;
  title: string;
  buyer: string;
  publicationDate: string | null;
  primaryCpvCode: string | null;
  primaryCpvTitle: string | null;
  formType: string | null;
  formTypeCode: string | null;
  deadlines: string[];
  estimatedValue: string | null;
  estimatedValueCurrency: string | null;
  detailUrl: string;
  feasibility: TenderFeasibilityRun | null;
  materialStatus: { total: number; downloaded: number };
  materialPlatforms: TenderMaterialPlatformInfo[];
  materialPlatformChecked: boolean;
};

type TenderFilters = {
  query: string;
  cpvCodes: string;
  formTypes: string;
  procedureTypes: string;
  buyerIds: string;
  estimatedValueMin: string;
  estimatedValueMax: string;
  publicationDateFrom: string;
  publicationDateTo: string;
  deadlineDateFrom: string;
  deadlineDateTo: string;
};

type SavedTenderSearch = {
  id: string;
  name: string;
  sources: string[];
  filters: Partial<TenderFilters>;
  sort: Record<string, unknown>;
  alert: {
    enabled: boolean;
    frequency: string | null;
    lastCheckedAt: string | null;
    lastResultFingerprint: string | null;
  };
};

const emptyFilters: TenderFilters = {
  query: "",
  cpvCodes: "",
  formTypes: "",
  procedureTypes: "",
  buyerIds: "",
  estimatedValueMin: "",
  estimatedValueMax: "",
  publicationDateFrom: "",
  publicationDateTo: "",
  deadlineDateFrom: "",
  deadlineDateTo: ""
};

const procedureOptions = [
  { value: "offentligt-udbud", label: "Open procedure" },
  { value: "begrænset-udbud", label: "Restricted procedure" },
  { value: "udbud-med-forhandling", label: "Negotiated procedure" },
  { value: "rammeaftale", label: "Framework agreement" },
  { value: "dynamisk-indkøbssystem", label: "Dynamic purchasing system" },
  { value: "miniudbud", label: "Mini competition" },
  { value: "markedsdialog", label: "Market dialogue" }
] as const;

const splitList = (value: string): string[] =>
  value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const TENDER_SEARCH_PAGE_SIZE = 100;

export const toSearchPayload = (filters: TenderFilters, page: number) => ({
  sources: ["udbud.dk"],
  query: filters.query.trim() || undefined,
  cpvCodes: splitList(filters.cpvCodes),
  formTypes: splitList(filters.formTypes),
  procedureTypes: splitList(filters.procedureTypes),
  buyerIds: splitList(filters.buyerIds),
  estimatedValueMin: filters.estimatedValueMin.trim() || undefined,
  estimatedValueMax: filters.estimatedValueMax.trim() || undefined,
  publicationDateFrom: filters.publicationDateFrom || undefined,
  publicationDateTo: filters.publicationDateTo || undefined,
  deadlineDateFrom: filters.deadlineDateFrom || undefined,
  deadlineDateTo: filters.deadlineDateTo || undefined,
  pageSize: TENDER_SEARCH_PAGE_SIZE,
  page,
  sortBy: "publicationDate",
  sortDirection: "desc"
});

export const visiblePageNumbers = (currentPage: number, totalPages: number): number[] => {
  const windowSize = 5;
  const end = Math.min(totalPages, Math.max(windowSize, currentPage + 2));
  const start = Math.max(1, Math.min(currentPage - 2, end - windowSize + 1));
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const parseJsonArray = (value: string): string[] => {
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
};

const formatDate = (value: string | null): string => (value ? value.slice(0, 10) : "-");
const formatValue = (value: string | null, currency: string | null): string =>
  value ? `${Number(value).toLocaleString("da-DK")} ${currency ?? ""}`.trim() : "-";

const feasibilityLabel = (run: TenderFeasibilityRun | null): string => {
  if (!run) return "Not rated";
  if (run.status === "failed") return "Failed";
  if (run.status === "running") return "Running";
  return run.score === null ? "Rated" : `${Math.round(run.score)}/100`;
};

const materialPlatformsLabel = (platforms: TenderMaterialPlatformInfo[] | undefined, checked = false): string => {
  const values = platforms ?? [];
  return values.length > 0
    ? values
        .map((platform) => `${platform.domain} (${platform.supportedAutoDownload ? "auto-download" : "manual only"})`)
        .join(", ")
    : checked
      ? "No platform found"
      : "Not checked";
};

const MaterialPlatformCell = ({ result }: { result: TenderSearchResult }): JSX.Element => {
  const platforms = result.materialPlatforms ?? [];
  const checked = result.materialPlatformChecked ?? false;
  if (platforms.length === 0) {
    return (
      <div className="space-y-1.5">
        <Badge variant="outline">{checked ? "No platform found" : "Not checked"}</Badge>
        {result.materialStatus.total > 0 ? (
          <p className="text-xs text-muted-foreground">
            {result.materialStatus.downloaded}/{result.materialStatus.total} files
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {platforms.map((platform) => (
        <div key={`${platform.domain}:${platform.platform}:${platform.supportedAutoDownload}`} className="flex flex-wrap gap-1">
          <Badge variant="outline">{platform.domain}</Badge>
          <Badge variant={platform.supportedAutoDownload ? "secondary" : "outline"}>
            {platform.supportedAutoDownload ? "Auto-download" : "Manual only"}
          </Badge>
        </div>
      ))}
      {result.materialStatus.total > 0 ? (
        <p className="text-xs text-muted-foreground">
          {result.materialStatus.downloaded}/{result.materialStatus.total} files
        </p>
      ) : null}
    </div>
  );
};

const formatDateTime = (value: string): string =>
  new Intl.DateTimeFormat("da-DK", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));

export const TendersPage = (): JSX.Element => {
  const [filters, setFilters] = useState<TenderFilters>(emptyFilters);
  const [activeSearchFilters, setActiveSearchFilters] = useState<TenderFilters>(emptyFilters);
  const [results, setResults] = useState<TenderSearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenderIds, setSelectedTenderIds] = useState<string[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedTenderSearch[]>([]);
  const [saveName, setSaveName] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [isRunningFeasibility, setIsRunningFeasibility] = useState(false);
  const [isStartingProposal, setIsStartingProposal] = useState(false);
  const [isSavedSearchPanelOpen, setIsSavedSearchPanelOpen] = useState(false);
  const [openFeasibilityResult, setOpenFeasibilityResult] = useState<TenderSearchResult | null>(null);

  const selectableResults = useMemo(() => results.filter((result) => result.id), [results]);
  const selectedResults = useMemo(
    () => results.filter((result) => result.id && selectedTenderIds.includes(result.id)),
    [results, selectedTenderIds]
  );
  const pageSize = TENDER_SEARCH_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
  const firstResultNumber = totalResults === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastResultNumber = Math.min(totalResults, (currentPage - 1) * pageSize + results.length);
  const pageNumbers = visiblePageNumbers(currentPage, totalPages);

  const updateFilter = (key: keyof TenderFilters, value: string): void => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const refreshSavedSearches = async (): Promise<void> => {
    const response = await fetch("/api/tenders/saved-searches");
    if (!response.ok) return;
    const payload = (await response.json()) as { searches: SavedTenderSearch[] };
    setSavedSearches(payload.searches);
  };

  const runSearch = async (page = 1, searchFilters = filters): Promise<void> => {
    setIsSearching(true);
    setStatusMessage("Searching tenders...");
    const response = await fetch("/api/tenders/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(toSearchPayload(searchFilters, page))
    });
    setIsSearching(false);
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({ error: "Tender search failed" }))) as { error?: string };
      setStatusMessage(payload.error ?? "Tender search failed");
      return;
    }
    const payload = (await response.json()) as { results: TenderSearchResult[]; totalResults: number };
    setResults(payload.results);
    setTotalResults(payload.totalResults);
    setActiveSearchFilters(searchFilters);
    setCurrentPage(page);
    setSelectedTenderIds([]);
    setStatusMessage(`Found ${payload.totalResults.toLocaleString("da-DK")} tender${payload.totalResults === 1 ? "" : "s"}`);
  };

  const saveSearch = async (): Promise<void> => {
    const name = saveName.trim();
    if (!name) {
      setStatusMessage("Name the search before saving");
      return;
    }
    setIsSavingSearch(true);
    const response = await fetch("/api/tenders/saved-searches", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        sources: ["udbud.dk"],
        filters,
        sort: { sortBy: "publicationDate", sortDirection: "desc" },
        alert: { enabled: false, frequency: "daily" }
      })
    });
    setIsSavingSearch(false);
    if (!response.ok) {
      setStatusMessage("Could not save search");
      return;
    }
    setSaveName("");
    setStatusMessage("Search saved");
    await refreshSavedSearches();
  };

  const deleteSavedSearch = async (searchId: string): Promise<void> => {
    await fetch(`/api/tenders/saved-searches/${searchId}`, { method: "DELETE" });
    await refreshSavedSearches();
  };

  const loadSavedSearch = (search: SavedTenderSearch): void => {
    setFilters({ ...emptyFilters, ...search.filters });
    setCurrentPage(1);
    setStatusMessage(`Loaded ${search.name}`);
  };

  const toggleTenderSelection = (tenderId: string, checked: boolean): void => {
    setSelectedTenderIds((current) => {
      if (checked) return current.includes(tenderId) ? current : [...current, tenderId];
      return current.filter((id) => id !== tenderId);
    });
  };

  const applyFeasibilityRuns = (runs: TenderFeasibilityRun[]): void => {
    setResults((current) =>
      current.map((result) => {
        const run = runs.find((candidate) => candidate.tender_id === result.id);
        return run ? { ...result, feasibility: run } : result;
      })
    );
  };

  const fetchFeasibilityRuns = async (tenderIds: string[]): Promise<TenderFeasibilityRun[]> => {
    const params = new URLSearchParams();
    params.set("ids", tenderIds.join(","));
    const response = await fetch(`/api/tenders/feasibility?${params.toString()}`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as { runs?: TenderFeasibilityRun[] };
    return Array.isArray(payload.runs) ? payload.runs : [];
  };

  const pollFeasibilityRuns = async (tenderIds: string[]): Promise<TenderFeasibilityRun[]> => {
    const maxAttempts = 120;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await sleep(attempt === 0 ? 1000 : 3000);
      const runs = await fetchFeasibilityRuns(tenderIds);
      if (runs.length > 0) {
        applyFeasibilityRuns(runs);
        const completedCount = runs.filter((run) => run.status === "succeeded" || run.status === "failed").length;
        setStatusMessage(`Running feasibility... ${completedCount}/${tenderIds.length} complete`);
        if (completedCount >= tenderIds.length) {
          return runs;
        }
      }
    }
    return await fetchFeasibilityRuns(tenderIds);
  };

  const runFeasibility = async (): Promise<void> => {
    if (selectedTenderIds.length === 0) return;
    const tenderIds = [...selectedTenderIds];
    setIsRunningFeasibility(true);
    setStatusMessage("Starting feasibility...");
    try {
      const response = await fetch("/api/tenders/feasibility", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenderIds })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({ error: "Feasibility failed" }))) as { error?: string };
        setStatusMessage(payload.error ?? "Feasibility failed");
        return;
      }
      const payload = (await response.json()) as { runs: TenderFeasibilityRun[] };
      applyFeasibilityRuns(payload.runs);
      setStatusMessage(`Running feasibility for ${tenderIds.length} tender${tenderIds.length === 1 ? "" : "s"}...`);
      const finalRuns = await pollFeasibilityRuns(tenderIds);
      const failedCount = finalRuns.filter((run) => run.status === "failed").length;
      const completedCount = finalRuns.filter((run) => run.status === "succeeded" || run.status === "failed").length;
      setStatusMessage(
        failedCount > 0
          ? `Feasibility completed with ${failedCount} failed tender${failedCount === 1 ? "" : "s"}`
          : `Rated ${completedCount} tender${completedCount === 1 ? "" : "s"}`
      );
    } catch {
      setStatusMessage("Feasibility failed to start");
    } finally {
      setIsRunningFeasibility(false);
    }
  };

  const startProposal = async (): Promise<void> => {
    const [selected] = selectedResults;
    if (!selected?.id) return;
    setIsStartingProposal(true);
    setStatusMessage("Downloading material and starting proposal...");
    const response = await fetch("/api/tenders/start-proposal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenderId: selected.id })
    });
    setIsStartingProposal(false);
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({ error: "Could not start proposal" }))) as { error?: string };
      setStatusMessage(payload.error ?? "Could not start proposal");
      return;
    }
    const payload = (await response.json()) as { caseId: string };
    window.history.pushState(null, "", `/cases/${encodeURIComponent(payload.caseId)}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  useEffect(() => {
    void refreshSavedSearches();
  }, []);

  const openFeasibility = openFeasibilityResult?.feasibility ?? null;
  const openPros = openFeasibility ? parseJsonArray(openFeasibility.pros_json) : [];
  const openCons = openFeasibility ? parseJsonArray(openFeasibility.cons_json) : [];

  return (
    <div className="space-y-4 pb-8">
      <section className="rounded-lg border bg-card p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <label className="space-y-1.5 md:col-span-2">
            <span className="text-xs font-medium text-muted-foreground">Free text</span>
            <Input value={filters.query} onChange={(event) => updateFilter("query", event.target.value)} placeholder="software, design, web..." />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">CPV codes</span>
            <Input value={filters.cpvCodes} onChange={(event) => updateFilter("cpvCodes", event.target.value)} placeholder="72000000" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Buyer CVR</span>
            <Input value={filters.buyerIds} onChange={(event) => updateFilter("buyerIds", event.target.value)} placeholder="comma separated" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Type/form</span>
            <Input value={filters.formTypes} onChange={(event) => updateFilter("formTypes", event.target.value)} placeholder="formular type" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Process type</span>
            <select
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={filters.procedureTypes}
              onChange={(event) => updateFilter("procedureTypes", event.target.value)}
            >
              <option value="">Any process</option>
              {procedureOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Value min</span>
            <Input value={filters.estimatedValueMin} onChange={(event) => updateFilter("estimatedValueMin", event.target.value)} inputMode="numeric" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Value max</span>
            <Input value={filters.estimatedValueMax} onChange={(event) => updateFilter("estimatedValueMax", event.target.value)} inputMode="numeric" />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Published from</span>
            <Input type="date" value={filters.publicationDateFrom} onChange={(event) => updateFilter("publicationDateFrom", event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Published to</span>
            <Input type="date" value={filters.publicationDateTo} onChange={(event) => updateFilter("publicationDateTo", event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Deadline from</span>
            <Input type="date" value={filters.deadlineDateFrom} onChange={(event) => updateFilter("deadlineDateFrom", event.target.value)} />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Deadline to</span>
            <Input type="date" value={filters.deadlineDateTo} onChange={(event) => updateFilter("deadlineDateTo", event.target.value)} />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={() => void runSearch(1, filters)} disabled={isSearching}>
            {isSearching ? <LoaderCircle className="size-4 animate-spin" /> : <Search className="size-4" />}
            Search
          </Button>
          <Button variant="outline" onClick={() => setIsSavedSearchPanelOpen(true)}>
            <Save className="size-4" />
            Save search
          </Button>
          <Button variant="outline" onClick={() => setIsSavedSearchPanelOpen(true)}>
            <Bookmark className="size-4" />
            Saved searches
            {savedSearches.length > 0 ? <Badge variant="secondary">{savedSearches.length}</Badge> : null}
          </Button>
          <Sheet modal={false} open={isSavedSearchPanelOpen} onOpenChange={setIsSavedSearchPanelOpen}>
            <SheetContent
              className="sm:max-w-md"
              showOverlay={false}
              style={{ width: "min(28rem, calc(100vw - 1rem))" }}
            >
              <SheetHeader className="border-b">
                <SheetTitle>Saved Searches</SheetTitle>
                <SheetDescription>Personal search filters saved for repeated tender discovery.</SheetDescription>
              </SheetHeader>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-4">
                <form
                  className="space-y-3 rounded-md border bg-muted/15 p-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveSearch();
                  }}
                >
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground" htmlFor="saved-search-name">
                      Search name
                    </label>
                    <Input
                      id="saved-search-name"
                      value={saveName}
                      onChange={(event) => setSaveName(event.target.value)}
                      placeholder="Name this search"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSavingSearch}>
                    {isSavingSearch ? <LoaderCircle className="size-4 animate-spin" /> : <Save className="size-4" />}
                    Confirm save
                  </Button>
                </form>

                {savedSearches.length === 0 ? (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                    No saved searches yet. Name the current filters and save them to reuse the search later.
                  </div>
                ) : (
                  savedSearches.map((search) => (
                    <div key={search.id} className="space-y-3 rounded-md border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{search.name}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{search.sources.join(", ") || "udbud.dk"}</p>
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => void deleteSavedSearch(search.id)}
                          aria-label={`Delete ${search.name}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(search.filters)
                          .filter(([, value]) => typeof value === "string" && value.trim().length > 0)
                          .slice(0, 4)
                          .map(([key, value]) => (
                            <Badge key={key} variant="outline">
                              {key}: {value}
                            </Badge>
                          ))}
                      </div>
                      <Button
                        type="button"
                        className="w-full"
                        variant="outline"
                        onClick={() => {
                          loadSavedSearch(search);
                          setIsSavedSearchPanelOpen(false);
                        }}
                      >
                        Load search
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </SheetContent>
          </Sheet>
          {statusMessage ? <Badge variant="secondary">{statusMessage}</Badge> : null}
        </div>
      </section>

      <section className="rounded-lg border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelectedTenderIds(selectableResults.map((result) => result.id).filter((id): id is string => Boolean(id)))}>
              Select all
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelectedTenderIds([])}>
              Select none
            </Button>
            <span className="text-sm text-muted-foreground">{selectedTenderIds.length} selected</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" disabled={selectedTenderIds.length === 0 || isRunningFeasibility} onClick={() => void runFeasibility()}>
              {isRunningFeasibility ? <LoaderCircle className="size-4 animate-spin" /> : <ClipboardCheck className="size-4" />}
              Run feasibility
            </Button>
            <Button size="sm" disabled={selectedResults.length !== 1 || isStartingProposal} onClick={() => void startProposal()}>
              {isStartingProposal ? <LoaderCircle className="size-4 animate-spin" /> : <DownloadCloud className="size-4" />}
              Start proposal
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Tender</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>CPV</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Feasibility</TableHead>
                <TableHead>Material platform</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-28 text-center text-muted-foreground">
                    Run a search to see tenders.
                  </TableCell>
                </TableRow>
              ) : (
                results.map((result) => {
                  const id = result.id ?? "";
                  const pros = result.feasibility ? parseJsonArray(result.feasibility.pros_json) : [];
                  const cons = result.feasibility ? parseJsonArray(result.feasibility.cons_json) : [];
                  return (
                    <TableRow key={`${result.noticeId}:${result.noticeVersion}`}>
                      <TableCell>
                        <Checkbox
                          checked={Boolean(id && selectedTenderIds.includes(id))}
                          disabled={!id}
                          onCheckedChange={(checked) => id && toggleTenderSelection(id, checked === true)}
                          aria-label={`Select ${result.title}`}
                        />
                      </TableCell>
                      <TableCell className="min-w-80">
                        <a href={result.detailUrl} target="_blank" rel="noopener" className="font-medium underline-offset-4 hover:underline">
                          {result.title}
                        </a>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <Badge variant="outline">{result.source}</Badge>
                          {result.formType ? <Badge variant="secondary">{result.formType}</Badge> : null}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-44">{result.buyer}</TableCell>
                      <TableCell>{formatDate(result.publicationDate)}</TableCell>
                      <TableCell>{formatDate(result.deadlines[0] ?? null)}</TableCell>
                      <TableCell>{[result.primaryCpvCode, result.primaryCpvTitle].filter(Boolean).join(" ") || "-"}</TableCell>
                      <TableCell>{formatValue(result.estimatedValue, result.estimatedValueCurrency)}</TableCell>
                      <TableCell className="min-w-48">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={result.feasibility?.status === "failed" ? "destructive" : "secondary"}>
                            {feasibilityLabel(result.feasibility)}
                          </Badge>
                          {result.feasibility ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => setOpenFeasibilityResult(result)}
                            >
                              <Eye className="size-3.5" />
                              View
                            </Button>
                          ) : null}
                        </div>
                        {pros.length > 0 || cons.length > 0 ? (
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {[pros[0], cons[0]].filter(Boolean).join(" / ")}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <MaterialPlatformCell result={result} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {totalResults > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Showing {firstResultNumber.toLocaleString("da-DK")}-{lastResultNumber.toLocaleString("da-DK")} of{" "}
              {totalResults.toLocaleString("da-DK")} tenders
            </p>
            <nav className="flex flex-wrap items-center gap-1" aria-label="Tender search pages">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isSearching || currentPage <= 1}
                onClick={() => void runSearch(1, activeSearchFilters)}
              >
                First
              </Button>
              <Button
                type="button"
                size="icon"
                variant="outline"
                disabled={isSearching || currentPage <= 1}
                onClick={() => void runSearch(currentPage - 1, activeSearchFilters)}
                aria-label="Previous page"
              >
                <ChevronLeft className="size-4" />
              </Button>
              {pageNumbers.map((pageNumber) => (
                <Button
                  key={pageNumber}
                  type="button"
                  size="sm"
                  variant={pageNumber === currentPage ? "default" : "outline"}
                  disabled={isSearching}
                  onClick={() => void runSearch(pageNumber, activeSearchFilters)}
                  aria-current={pageNumber === currentPage ? "page" : undefined}
                >
                  {pageNumber}
                </Button>
              ))}
              <Button
                type="button"
                size="icon"
                variant="outline"
                disabled={isSearching || currentPage >= totalPages}
                onClick={() => void runSearch(currentPage + 1, activeSearchFilters)}
                aria-label="Next page"
              >
                <ChevronRight className="size-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isSearching || currentPage >= totalPages}
                onClick={() => void runSearch(totalPages, activeSearchFilters)}
              >
                Last
              </Button>
              <span className="px-2 text-sm text-muted-foreground">
                Page {currentPage.toLocaleString("da-DK")} of {totalPages.toLocaleString("da-DK")}
              </span>
            </nav>
          </div>
        ) : null}
      </section>

      <Dialog open={openFeasibilityResult !== null} onOpenChange={(open) => !open && setOpenFeasibilityResult(null)}>
        <DialogContent className="max-h-[min(760px,calc(100svh-2rem))] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Feasibility Analysis</DialogTitle>
            <DialogDescription>{openFeasibilityResult?.title ?? "Tender result"}</DialogDescription>
          </DialogHeader>

          {openFeasibility && openFeasibilityResult ? (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border p-3">
                  <p className="text-xs font-medium text-muted-foreground">Score</p>
                  <p className="mt-1 text-2xl font-semibold">
                    {openFeasibility.score === null ? "-" : `${Math.round(openFeasibility.score)}/100`}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs font-medium text-muted-foreground">Status</p>
                  <p className="mt-2">
                    <Badge variant={openFeasibility.status === "failed" ? "destructive" : "secondary"}>
                      {feasibilityLabel(openFeasibility)}
                    </Badge>
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs font-medium text-muted-foreground">Created</p>
                  <p className="mt-2 text-sm">{formatDateTime(openFeasibility.created_at)}</p>
                </div>
              </div>

              {openFeasibility.summary ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">Summary</h3>
                  <p className="rounded-md border bg-muted/20 p-3 text-sm leading-relaxed">{openFeasibility.summary}</p>
                </section>
              ) : null}

              {openFeasibility.error ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">Error</h3>
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm leading-relaxed text-destructive">
                    {openFeasibility.error}
                  </p>
                </section>
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">Pros</h3>
                  {openPros.length > 0 ? (
                    <ul className="space-y-2">
                      {openPros.map((item) => (
                        <li key={item} className="rounded-md border bg-muted/20 p-3 text-sm leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="rounded-md border p-3 text-sm text-muted-foreground">No pros were returned.</p>
                  )}
                </section>

                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">Cons</h3>
                  {openCons.length > 0 ? (
                    <ul className="space-y-2">
                      {openCons.map((item) => (
                        <li key={item} className="rounded-md border bg-muted/20 p-3 text-sm leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="rounded-md border p-3 text-sm text-muted-foreground">No cons were returned.</p>
                  )}
                </section>
              </div>

              <section className="grid gap-2 border-t pt-4 text-sm text-muted-foreground sm:grid-cols-2">
                <p>Buyer: {openFeasibilityResult.buyer}</p>
                <p>Source: {openFeasibilityResult.source}</p>
                <p>Notice: {openFeasibilityResult.noticePublicationNumber || openFeasibilityResult.noticeId}</p>
                <p>Material: {openFeasibilityResult.materialStatus.downloaded}/{openFeasibilityResult.materialStatus.total}</p>
                <p>Platform: {materialPlatformsLabel(openFeasibilityResult.materialPlatforms, openFeasibilityResult.materialPlatformChecked)}</p>
              </section>
            </div>
          ) : null}

          <DialogFooter showCloseButton />
        </DialogContent>
      </Dialog>
    </div>
  );
};
